/**
 * Info 파일 생성 유틸리티
 * 프로젝트와 금형의 정보 파일(_project_info.txt, _mold_info.txt)을 생성
 */

import { Project } from '../types/project.types';
import { Mold } from '../types';
import { Timestamp } from 'firebase/firestore';

/**
 * Timestamp를 문자열로 변환
 */
const formatTimestamp = (timestamp?: Timestamp | Date | null): string => {
  if (!timestamp) return '-';
  
  try {
    const date = timestamp instanceof Timestamp 
      ? timestamp.toDate() 
      : timestamp;
    
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return '-';
  }
};

/**
 * 프로젝트 정보를 텍스트로 포맷
 */
export const formatProjectInfo = (project: Project): string => {
  const lines = [
    '='.repeat(60),
    '프로젝트 정보',
    '='.repeat(60),
    '',
    `프로젝트 번호: ${project.projectNo || project.projectId || '-'}`,
    `프로젝트명: ${project.projectName || '-'}`,
    `프로젝트 타입: ${project.projectType || '-'}`,
    '',
    '--- 거래처 정보 ---',
    `거래처명: ${project.customerName || '-'}`,
    `거래처 ID: ${project.customerId || '-'}`,
    '',
    '--- 상태 및 일정 ---',
    `상태: ${project.status || '-'}`,
    `시작일: ${formatTimestamp(project.startDate)}`,
    `목표일: ${formatTimestamp(project.targetDate)}`,
    `입고예정일: ${formatTimestamp(project.scheduledDate)}`,
    `출고일: ${formatTimestamp(project.completedDate)}`,
    '',
    '--- 금형 정보 ---',
    `관련 금형 수: ${project.moldCount || 0}개`,
    `금형 ID 목록: ${project.moldIds?.join(', ') || '-'}`,
    '',
    '--- 파일 정보 ---',
    `파일 존재: ${project.hasFiles ? '있음' : '없음'}`,
    `파일 개수: ${project.fileCount || 0}개`,
    '',
    '--- 추가 정보 ---',
    `우선순위: ${project.priority || '-'}`,
    `태그: ${project.tags?.join(', ') || '-'}`,
    '',
    '--- 설명 및 비고 ---',
  ];

  if (project.description) {
    lines.push(`설명: ${project.description}`);
  }
  
  if (project.notes) {
    lines.push(`비고: ${project.notes}`);
  }

  lines.push('');
  lines.push('--- 메타데이터 ---');
  lines.push(`생성일: ${formatTimestamp(project.createdAt)}`);
  lines.push(`수정일: ${formatTimestamp(project.updatedAt)}`);
  lines.push(`생성자: ${project.createdBy || '-'}`);
  lines.push('');
  lines.push('='.repeat(60));
  lines.push(`생성: EGMMS V3 자동 생성 (${new Date().toLocaleString('ko-KR')})`);
  lines.push('='.repeat(60));

  return lines.join('\n');
};

/**
 * 금형 정보를 텍스트로 포맷
 */
export const formatMoldInfo = (mold: Mold): string => {
  const lines = [
    '='.repeat(60),
    '금형 정보',
    '='.repeat(60),
    '',
    `금형 번호: ${mold.moldNumber || '-'}`,
    `금형 ID: ${mold.id || '-'}`,
    '',
    '--- 프로젝트 정보 ---',
    `프로젝트 번호: ${mold.projectNo || '-'}`,
    `프로젝트 ID: ${mold.projectId || '-'}`,
    '',
    '--- 거래처 정보 ---',
    `거래처명: ${mold.customerName || '-'}`,
    `거래처 ID: ${mold.customerId || '-'}`,
    '',
    '--- 상태 및 위치 ---',
    `상태: ${mold.status || '-'}`,
    `보관장소: ${mold.location || '-'}`,
    `보관장소 ID: ${mold.locationId || '-'}`,
    '',
    '--- QR 코드 ---',
    `QR 코드: ${mold.qrCode || '-'}`,
    '',
  ];

  // 추가 필드들 (있으면 표시)
  const additionalFields = [
    'moldName',
    'moldType',
    'cavityCount',
    'material',
    'weight',
    'size',
    'manufacturer',
    'manufactureDate',
    'notes',
  ];

  const hasAdditionalInfo = additionalFields.some(field => mold[field]);
  
  if (hasAdditionalInfo) {
    lines.push('--- 추가 정보 ---');
    
    if (mold.moldName) lines.push(`금형명: ${mold.moldName}`);
    if (mold.moldType) lines.push(`금형 타입: ${mold.moldType}`);
    if (mold.cavityCount) lines.push(`캐비티 수: ${mold.cavityCount}개`);
    if (mold.material) lines.push(`재질: ${mold.material}`);
    if (mold.weight) lines.push(`중량: ${mold.weight}`);
    if (mold.size) lines.push(`크기: ${mold.size}`);
    if (mold.manufacturer) lines.push(`제조사: ${mold.manufacturer}`);
    if (mold.manufactureDate) lines.push(`제조일: ${formatTimestamp(mold.manufactureDate)}`);
    if (mold.notes) lines.push(`비고: ${mold.notes}`);
    
    lines.push('');
  }

  lines.push('--- 메타데이터 ---');
  lines.push(`생성일: ${formatTimestamp(mold.createdAt)}`);
  lines.push(`수정일: ${formatTimestamp(mold.updatedAt)}`);
  lines.push('');
  lines.push('='.repeat(60));
  lines.push(`생성: EGMMS V3 자동 생성 (${new Date().toLocaleString('ko-KR')})`);
  lines.push('='.repeat(60));

  return lines.join('\n');
};
